import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Check,
  Sparkles,
  MessageCircle,
  Users,
  AlertTriangle,
  Target,
  Heart,
  TrendingUp,
  Shield,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { getPublicStoreCatalog, type StoreCatalogItem } from '../lib/db-storage';
import { SINGLES_BUNDLE_MEMBER_IDS } from '../lib/store-catalog-constants';

function findCatalogPack(items: StoreCatalogItem[], packId: string): StoreCatalogItem | undefined {
  const pid = packId.toLowerCase();
  return items.find((i) => {
    if (i.kind !== 'pack' || i.active === false || !i.stripe_price_id) return false;
    const slugNorm = String(i.slug || '').replace(/^pack-/, '').replace(/^singles-/, '').toLowerCase();
    if (slugNorm === pid || String(i.slug).toLowerCase() === pid) return true;
    const ps = i.pack_slugs;
    return Array.isArray(ps) && ps.some((s) => String(s).toLowerCase() === pid);
  });
}

function formatMoney(cents: number | null | undefined, fallback: string): string {
  if (cents == null || !Number.isFinite(cents)) return fallback;
  return `$${(cents / 100).toFixed(2)}`;
}

export default function SinglesPacks() {
  const [loading, setLoading] = useState<string | null>(null);
  const [catalogItems, setCatalogItems] = useState<StoreCatalogItem[]>([]);
  const auth = useAuth();
  const navigate = useNavigate();

  const user = auth?.user || null;
  const accessToken = auth?.accessToken || null;

  useEffect(() => {
    getPublicStoreCatalog()
      .then((r) => setCatalogItems(r.items || []))
      .catch(() => setCatalogItems([]));
  }, []);

  const singlesPacks = useMemo(
    () =>
      [
        {
          id: 'dating-strategy',
          title: 'Connection Strategy Pack',
          defaultPriceLabel: '$4.99',
          description: 'Build healthier connection habits with evidence-based strategies',
          icon: Target,
          color: 'from-violet-500 to-purple-500',
          features: [
            'Attachment-based connection approach guide',
            'First 3 conversations strategy framework',
            'When to have important conversations',
            'Avoid common anxious/avoidant traps',
            'Texting strategy & response timing',
            'Pacing and boundary-setting guidelines',
            'How to spot early misalignment',
            '30-day connection action plan',
          ],
        },
        {
          id: 'profile-optimization',
          title: 'Profile Optimization Pack',
          defaultPriceLabel: '$3.99',
          description: 'Create a profile that attracts aligned, healthy connections',
          icon: Sparkles,
          color: 'from-pink-500 to-rose-500',
          features: [
            'Photo selection strategy',
            'Bio writing templates',
            'Prompt response examples',
            'Values-based profile audit',
            'Attracting secure partners',
            'Filtering misaligned connections',
            'Profile refresh checklist',
            'A/B testing framework',
          ],
        },
        {
          id: 'red-flags-assessment',
          title: 'Red Flags Assessment',
          defaultPriceLabel: '$2.99',
          description: 'Learn to identify deal-breakers and warning signs early',
          icon: AlertTriangle,
          color: 'from-red-500 to-orange-500',
          features: [
            'Personalized red flag checklist',
            'Early warning signs by attachment style',
            'Love bombing vs. genuine interest',
            'Emotional unavailability indicators',
            'Breadcrumbing & ghosting patterns',
            'When to walk away guide',
            'Gut instinct validation framework',
            'Past pattern analysis',
          ],
        },
        {
          id: 'first-date-guide',
          title: 'First Connection Guide',
          defaultPriceLabel: '$2.99',
          description: 'Navigate early interactions with confidence and clarity',
          icon: MessageCircle,
          color: 'from-blue-500 to-cyan-500',
          features: [
            'Pre-meet preparation checklist',
            'Conversation starters by values',
            'Questions to assess alignment',
            'How to read body language',
            'Green flags to look for',
            'Post-meet evaluation framework',
            'Second meet decision matrix',
            'Rejection handling strategies',
          ],
        },
        {
          id: 'compatibility-deep-dive',
          title: 'Alignment Deep Dive',
          defaultPriceLabel: '$5.99',
          description: 'Advanced analysis of your alignment patterns and preferences',
          icon: Users,
          color: 'from-green-500 to-emerald-500',
          features: [
            'Detailed alignment scoring system',
            'Attachment alignment matrix',
            'Values alignment assessment',
            'Communication style alignment',
            'Lifestyle alignment quiz',
            'Long-term potential indicators',
            'Deal-breaker vs. preference sorting',
            'Relationship readiness assessment',
          ],
        },
        {
          id: 'breakup-prevention',
          title: 'Early Relationship Success',
          defaultPriceLabel: '$4.99',
          description: 'Navigate the critical first 3 months without self-sabotage',
          icon: Heart,
          color: 'from-amber-500 to-yellow-500',
          features: [
            ' 0-3 month relationship milestones',
            'Healthy vs. anxious attachment behaviors',
            'How to build secure foundation',
            'Managing expectations timeline',
            'Communication frequency guidelines',
            'Vulnerability pacing strategy',
            'Common self-sabotage patterns',
            'Building lasting attraction',
          ],
        },
      ] as const,
    [],
  );

  const bundlePricing = useMemo(() => {
    let totalCents = 0;
    let allConfigured = true;
    for (const id of SINGLES_BUNDLE_MEMBER_IDS) {
      const row = findCatalogPack(catalogItems, id);
      if (!row?.amount_cents) {
        allConfigured = false;
        break;
      }
      totalCents += row.amount_cents;
    }
    return { totalCents, allConfigured };
  }, [catalogItems]);

  const handlePurchase = async (packId: string) => {
    if (packId === 'singles-bundle') {
      if (!bundlePricing.allConfigured) {
        toast.error(
          'The bundle is not available yet. Configure all singles packs in Admin → Storefront (Stripe prices required).',
        );
        return;
      }
    } else {
      const cat = findCatalogPack(catalogItems, packId);
      if (!cat?.stripe_price_id) {
        toast.error(
          'This pack is not available for purchase yet. Enable it in Admin → Storefront (Stripe price required).',
        );
        return;
      }
    }

    if (!user) {
      toast.error('Please sign in to purchase');
      navigate('/auth');
      return;
    }

    if (!accessToken) {
      toast.error('Session expired. Please sign in again.');
      navigate('/auth');
      return;
    }

    setLoading(packId);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/create-pack-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}`,
            'X-User-Token': accessToken,
          },
          body: JSON.stringify({
            packId,
            packType: 'singles',
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Checkout failed');
        setLoading(null);
        return;
      }

      if (data.url) {
        toast.success('Opening checkout...');
        const stripeWindow = window.open(data.url, '_blank');

        if (!stripeWindow) {
          toast.error('Please allow popups to complete checkout');
        }

        setLoading(null);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Network error. Please try again.');
      setLoading(null);
    }
  };

  const featured = singlesPacks[0];
  const featuredRow = findCatalogPack(catalogItems, featured.id);
  const featuredPrice = formatMoney(featuredRow?.amount_cents, featured.defaultPriceLabel);

  return (
    <div className="min-h-screen tension-page-bg">
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/compatibility-profile')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <div>
            <h1 className="text-xl font-light">Singles Strategy Packs</h1>
            <p className="text-sm text-neutral-600">One-time purchases for relationship readiness</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-light text-neutral-900">Connect Smarter, Not Harder</h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Expert guidance for every stage of your relationship-readiness journey. One-time purchases, lifetime access. Prices reflect
            your live store catalog (Stripe).
          </p>
        </div>

        <Card className="p-8 bg-gradient-to-br from-violet-600 to-purple-600 text-white relative overflow-hidden">
          <div className="absolute top-4 right-4">
            <Badge className="bg-white text-violet-600">MOST POPULAR</Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-medium mb-2">{featured.title}</h3>
                <p className="text-violet-100">{featured.description}</p>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-light">{featuredPrice}</span>
                <span className="text-violet-200">one-time</span>
              </div>
              <Button
                onClick={() => handlePurchase(featured.id)}
                className="bg-white text-violet-600 hover:bg-neutral-100"
                disabled={loading === featured.id}
              >
                {loading === featured.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Get Connection Strategy Pack'
                )}
              </Button>
            </div>

            <div className="space-y-3">
              <p className="font-medium">Includes:</p>
              <ul className="space-y-2 text-sm text-violet-100">
                {featured.features.slice(0, 5).map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
                <li className="text-violet-200">+ 3 more...</li>
              </ul>
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {singlesPacks.slice(1).map((pack) => {
            const Icon = pack.icon;
            const row = findCatalogPack(catalogItems, pack.id);
            const priceLabel = formatMoney(row?.amount_cents, pack.defaultPriceLabel);
            return (
              <Card key={pack.id} className="p-6 space-y-4 hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 bg-gradient-to-br ${pack.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-neutral-900">{pack.title}</h3>
                  <p className="text-sm text-neutral-600">{pack.description}</p>
                  <p className="text-2xl font-light text-neutral-900">{priceLabel}</p>
                </div>

                <ul className="space-y-2 text-sm text-neutral-700">
                  {pack.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {pack.features.length > 4 && (
                    <li className="text-neutral-500">+ {pack.features.length - 4} more...</li>
                  )}
                </ul>

                <Button
                  onClick={() => handlePurchase(pack.id)}
                  className="w-full"
                  variant="outline"
                  disabled={loading === pack.id}
                >
                  {loading === pack.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Get This Pack'
                  )}
                </Button>
              </Card>
            );
          })}
        </div>

        <Card className="p-8 bg-gradient-to-br from-emerald-600 to-green-600 text-white">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-8 h-8" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <Badge className="bg-white text-emerald-600 mb-3">FULL LIBRARY</Badge>
                <h3 className="text-2xl font-medium mb-2">Complete Singles Bundle</h3>
                <p className="text-emerald-100">
                  One checkout adds all six packs to your account (same line items as buying each pack individually).
                </p>
              </div>

              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-4xl font-light">
                  {bundlePricing.allConfigured
                    ? formatMoney(bundlePricing.totalCents, '$—')
                    : '—'}
                </span>
                <span className="text-emerald-100">one-time (6 × catalog price)</span>
              </div>

              <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {singlesPacks.map((pack) => (
                  <div key={pack.id} className="flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span>{pack.title}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => handlePurchase('singles-bundle')}
                className="bg-white text-emerald-600 hover:bg-neutral-100"
                size="lg"
                disabled={loading === 'singles-bundle' || !bundlePricing.allConfigured}
              >
                {loading === 'singles-bundle' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Get Complete Bundle'
                )}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-8 bg-neutral-50 border-neutral-200">
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-center">Frequently Asked Questions</h3>

            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div className="space-y-2">
                <p className="font-medium text-neutral-900">What&apos;s included in each pack?</p>
                <p className="text-neutral-600">
                  Each pack includes detailed guides, templates, checklists, and action frameworks. All content is based
                  on attachment science and evidence-based relationship research.
                </p>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-neutral-900">Do I need a subscription?</p>
                <p className="text-neutral-600">
                  No! These are one-time purchases. Buy only what you need, when you need it. Premium subscription unlocks
                  additional features but isn&apos;t required.
                </p>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-neutral-900">Can I access packs offline?</p>
                <p className="text-neutral-600">
                  Yes. Once purchased, all content is available offline in the app. No internet connection required.
                </p>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-neutral-900">What if I&apos;m not satisfied?</p>
                <p className="text-neutral-600">
                  We offer a 14-day money-back guarantee. If a pack doesn&apos;t meet your expectations, contact support
                  for a full refund.
                </p>
              </div>
            </div>
          </div>
        </Card>

        <div className="text-center space-y-4">
          <p className="text-xs text-neutral-500">
            🔒 Secure payment powered by Stripe • 14-day money-back guarantee
          </p>
          <p className="text-xs text-neutral-500">Join 5,000+ singles who&apos;ve transformed their relationship approach</p>
        </div>

        <Card className="p-8 border-2 border-violet-200">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-violet-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-violet-600" />
            </div>
            <div>
              <h3 className="text-xl font-medium mb-2">Want Premium Features Too?</h3>
              <p className="text-neutral-600 max-w-xl mx-auto">
                Premium subscription includes advanced alignment analysis, personalized insights, and unlimited access
                to all future singles content.
              </p>
            </div>
            <Button onClick={() => navigate('/pricing')} variant="outline" size="lg">
              View Premium Plans
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
